import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  GENIE_DOCUMENT_FORMATS,
  type GenieDocumentFormat,
} from "@/types/genie-documents/genie-document";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieDocumentFormatGalleryProps {
  /** Highlighted card, when the gallery is being used as a picker. */
  selectedKey?: string | null;
  onSelect: (format: GenieDocumentFormat) => void;
  /** Compact = inside the builder sheet; roomy = the page's opening band. */
  variant?: "page" | "sheet";
  className?: string;
}

/**
 * The menu of things you can make from this Genie's knowledge.
 *
 * `planned` formats are shown and not offered — the shape is settled, the
 * generator template is not, and pretending otherwise would produce an
 * invented-looking document.
 */
export const GenieDocumentFormatGallery = ({
  selectedKey,
  onSelect,
  variant = "page",
  className,
}: GenieDocumentFormatGalleryProps) => (
  <div
    className={cn(
      "grid gap-3",
      variant === "page" ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2",
      className,
    )}
  >
    {GENIE_DOCUMENT_FORMATS.map((format) => {
      const planned = format.status === "planned";
      const active = selectedKey === format.key;

      return (
        <button
          key={format.key}
          type="button"
          disabled={planned}
          onClick={() => onSelect(format)}
          className={cn(
            "group relative flex flex-col rounded-xl border-2 p-4 text-left transition-all",
            planned
              ? "cursor-not-allowed border-dashed border-gray-200 bg-gray-50/60"
              : active
                ? "border-genie-primary bg-genie-primary/5"
                : "border-gray-200 bg-white hover:border-genie-primary/40 hover:shadow-sm",
          )}
        >
          <div className="mb-2.5 flex items-start justify-between gap-2">
            <GenieIcon
              name={format.genieIcon}
              size={32}
              className={cn(planned && "opacity-40 grayscale")}
            />
            {planned && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <Lock className="h-2.5 w-2.5" />
                Soon
              </span>
            )}
          </div>
          <p className={cn("text-sm font-semibold", planned ? "text-gray-500" : "text-gray-900")}>
            {format.label}
          </p>
          <p className="mt-1 text-xs leading-snug text-gray-500">{format.description}</p>
        </button>
      );
    })}
  </div>
);

export default GenieDocumentFormatGallery;
